import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import Liveries from './pages/Liveries';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css'; // for Google Fonts and styles
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RolesProvider } from './context/RolesContext';
import { AppConfigProvider, useAppConfig } from './context/AppConfigContext';
import { isAdminEmail } from './utils/admin';
import { db } from './firebase';
import Profile from './pages/Profile';
import Home from './pages/Home';
import Forums from './pages/Forums';
import Thread from './pages/Thread';
import Beliefs from './pages/Beliefs';
import Admin from './pages/Admin';
import Maintenance from './pages/Maintenance';
import SecretPen from './pages/SecretPen';


const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const SectionList = ({ sections, theme, expandedIds, handleExpand }) => {

  return (
    <>
      {sections.map((section) => {
        const slug = slugify(section.title);
        const id = section.title === 'Contact Me' ? 'contact'
                  : section.title.toLowerCase().includes('currently working') ? 'work'
                  : slug;

        return (
          <motion.div
            key={slug}
            id={id}
            whileHover={{ scale: 1.02 }}
            className="glass-card"
          >
            <h2
              className="card-title"
              data-theme={theme}
              role="button"
              tabIndex={0}
              onClick={() => handleExpand(slug)}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleExpand(slug);
                }
              }}
            >
              {section.title}
            </h2>

            <AnimatePresence>
              {expandedIds.includes(slug) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="card-body"
                  data-theme={theme}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {(() => {
                    const isThanks = section.title.toLowerCase().includes('thanks');
                    const contentNode = (typeof section.content === 'string')
                      ? <div dangerouslySetInnerHTML={{ __html: section.content }} />
                      : section.content;
                    return isThanks ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                        <div style={{ maxWidth: '60ch' }}>
                          {contentNode}
                        </div>
                      </div>
                    ) : contentNode;
                  })()}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </>
  );
};

const randomImages = [
  "https://website.cdn.tritrisim.com/compressed/737.jpg",
  "https://raw.githubusercontent.com/Something-Comicallyfunny/THESITEOFALIFETIME/main/Screenshot%202025-07-22%20214311.png",
  "https://raw.githubusercontent.com/Something-Comicallyfunny/THESITEOFALIFETIME/main/4E5270B9-4E99-449B-9CEE-D943622A5451.png",
  "https://raw.githubusercontent.com/Something-Comicallyfunny/THESITEOFALIFETIME/main/image.png"
];

const randomImage = randomImages[Math.floor(Math.random() * randomImages.length)];

const sections = [
    {
      title: 'Who is Something Comical?',
      content: 'I\'m Ezra, a detail-oriented and creative guy with a passion for technology and aviation. My interests range from developing custom effects in Microsoft Flight Simulator (TriTriSim.com) to contributing to tech in theatre productions. I bring enthusiasm, problem-solving skills, and a commitment to delivering high-quality results in every project I undertake.'
    },
    {
      title: 'Coding',
      content: 'I have a lot of experience in coding. From JS to C++, I have used them all. I also have experience with server backends and frontends.'
    },
    {
      title: 'Audio Mixing',
      content: 'One huge aspect of my life is music. I love music. With the love of music comes (at least for me) the love of making it sound good. I do audio mixing for a few local companies. I primarily mix on the Allen & Heath SQ5 and the Behringer X32.'
    },
    {
      title: 'Lighting',
      content: 'With the love of music and making it sound good also comes the love of making it look good. I love lighting and did lighting for a local company part-time for a few years. I\'m trained in ONYX by Obsidian Control Systems; however, I\'m looking to expand my knowledge into other software.'
    },
    {
      title: 'My involvement in TFX',
      content: 'I’m currently in the main 10 devs for TriTriSim TFX (TriTriSim.com). We help bring realistic FX to MSFS 2020 and 24. I love the TFX community, and I’m so excited to see what else our dev team can cook up.'
    },
    {
      title: 'Random photos and screenshots',
      content: (
        <img
          src={randomImage}
          alt="Random from TriTriSim"
          style={{ width: "100%", borderRadius: "12px" }}
        />
      )
    },
    { 
      title: 'Contact Me',
      content: `If you want to contact me, you can contact me on gmail <a href="https://gmail.com" style="color: rgba(255, 0, 0, 1); text-decoration: underline;">ezradixon@somethingcomical.com</a> or contact me on discord on my account <a href="https://discord.com/users/1244909861922410571" style="color: rgba(255, 0, 0, 1); text-decoration: underline;">SomethingComical</a>. I am currently looking for people to help with my projects and, If your willing, I would love to make you apart of my team! `
    }, 
    {
      title: 'Download TFX',
      content: (
        <div style={{ textAlign: "center" }}>
          <p>Wanna download TFX to see what it's all about? Click the link below!:</p>
          <a 
            href="https://installer.cdn.tritrisim.com/TriTriSim%20Installer%20Source%201.7.5.zip"   
            download 
            style={{
              display: "inline-block",
              padding: "12px 24px",
              backgroundColor: "#0004ffff",
              color: "white",
              borderRadius: "8px",
              textDecoration: "none",
              fontWeight: "bold",
              marginTop: "10px"
            }}
          >
            ⬇ Download TFX
          </a>
        </div>
      )
    },
    { 
      title: 'Last updated',
      content: '18/10/25 11:15 AM AEST'
    },
   {
  title: "What I'm currently working on",
  content: "I have given up on EMGT for now due to lack of time and resources. Instead, I'm focusing on smaller projects that I can manage more easily alongside my other commitments. Stay tuned for updates on these new ventures!"
    },
    {
      title: 'Thanks',
      content: 'I want to take the time to thank a few key people in the development of this site. I want to thank <a href="https://discord.com/users/1098292586839543919" style="color: rgba(255, 0, 0, 1); text-decoration: underline;">Ted</a> for helping me and encouradging me while I made the site and hosting the current background image. (he will also hopefully contribute to the prod in the future) I also want to thank <a href="https://discord.com/users/1388995060678529196" style="color: rgba(255, 0, 0, 1); text-decoration: underline;">Pepe</a> for uploading the first background I used and letting me piggy back off of it..  And I cant forget to mention <a href="https://discord.com/users/1373679841068711956" style="color: rgba(255, 0, 0, 1); text-decoration: underline;">TriTriTheCuber</a> for bug fixes. I also want to thank the TFX Team <a href="https://tritrisim.com" style="color: rgba(255, 0, 0, 1); text-decoration: underline;">(TriTriSim.com)</a> for being the amazing team that we are and for all the hard work we do to make TFX the best it can be. And finally, I want to thank <a href="YOU" style="color: rgba(255, 5, 105, 1); text-decoration: underline;">YOU</a> for visiting my site and taking the time to read this. I hope you have a great day!'
    },
];

function NavAuthButtons() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { adminEmails } = useAppConfig();
  const isAdmin = isAdminEmail(user?.email, adminEmails);
  const navClass = ({ isActive }) => `nav-link${isActive ? ' active' : ''}`;
  const onLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch {}
  };
  if (user) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {isAdmin ? (
          <NavLink className={navClass} to="/admin">Admin</NavLink>
        ) : null}
        <NavLink className={navClass} to="/profile" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {user.photoURL ? (
            <img src={user.photoURL} alt="avatar" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
          ) : null}
          <span style={{ opacity: 0.85 }}>{user.displayName || user.email}</span>
        </NavLink>
        <button onClick={onLogout} className="nav-link" type="button">
          Logout
        </button>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <NavLink className={navClass} to="/login">Login</NavLink>
      <NavLink className={navClass} to="/register">Sign Up</NavLink>
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const [theme] = useState('dark');
  const [expandedIds, setExpandedIds] = useState([]);
  const pageRef = useRef(null);
  const { loading } = useAuth();
  const {
    disabled: maintenanceDisabled,
    message: maintenanceMessage,
    endsAt: maintenanceEndsAt,
    updatedAt: maintenanceUpdatedAt,
    updatedBy: maintenanceUpdatedBy,
    initiatedBy: maintenanceInitiatedBy,
    banner,
    loadingConfig,
    adminEmails,
  } = useAppConfig();
  const { user } = useAuth();
  const isAdmin = isAdminEmail(user?.email, adminEmails);
  const [navOpen, setNavOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const maintenanceBypass = useMemo(() => new Set([
    '/login',
    '/register',
    '/reset-password',
    '/admin',
    '/secret',
  ]), []);
  const isBypassRoute = maintenanceBypass.has(location.pathname);

  const maintenanceEndsAtDate = useMemo(() => {
    try {
      if (!maintenanceEndsAt) return null;
      if (typeof maintenanceEndsAt?.toDate === 'function') return maintenanceEndsAt.toDate();
      const d = new Date(maintenanceEndsAt);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }, [maintenanceEndsAt]);

  const maintenanceExpired = useMemo(() => {
    if (!maintenanceDisabled) return false;
    if (!maintenanceEndsAtDate) return false;
    return now >= maintenanceEndsAtDate.getTime();
  }, [maintenanceDisabled, maintenanceEndsAtDate, now]);

  const effectiveMaintenanceDisabled = maintenanceDisabled && !maintenanceExpired;
  const bannerEnabled = !!banner?.enabled && !!String(banner?.text || '').trim();
  const bannerTone = banner?.tone || 'info';
  const bannerLinkLabel = banner?.linkLabel || 'Learn more';

  useEffect(() => {
    if (!maintenanceExpired) return;
    // Best-effort: once expired, try to flip the remote flag off.
    // Firestore rules enforce that this can only change `disabled` after `endsAt`.
    (async () => {
      try {
        await updateDoc(doc(db, 'config', 'app'), { disabled: false });
      } catch {
        // non-fatal
      }
    })();
  }, [maintenanceExpired]);

  useEffect(() => {
    if (!maintenanceDisabled || !maintenanceEndsAtDate) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [maintenanceDisabled, maintenanceEndsAtDate]);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const navLinkClass = ({ isActive }) => `nav-link${isActive ? ' active' : ''}`;
  const handleExpand = (id) => {
    // Open selected + its neighbor (pairing by visual adjacency in data order)
    const idx = sections.findIndex(s => slugify(s.title) === id);
    if (idx === -1) return;
    const neighborIdx = (idx % 2 === 0) ? idx + 1 : idx - 1;
    const idsToToggle = [id];
    if (neighborIdx >= 0 && neighborIdx < sections.length) {
      idsToToggle.push(slugify(sections[neighborIdx].title));
    }
    const currentlyOpen = new Set(expandedIds);
    const allOpen = idsToToggle.every(x => currentlyOpen.has(x));
    if (allOpen) {
      idsToToggle.forEach(x => currentlyOpen.delete(x));
    } else {
      idsToToggle.forEach(x => currentlyOpen.add(x));
    }
    setExpandedIds(Array.from(currentlyOpen));
  };
  const handleMouseMove = () => {};

  // Derive sections for grid and bottom "Thanks" card
  const mainSections = sections.filter(s => s.title.toLowerCase() !== 'thanks');
  const thanksSection = sections.find(s => s.title.toLowerCase() === 'thanks');

  if (loading || loadingConfig) {
    return <div style={{ paddingTop: '4rem', textAlign: 'center' }}>Loading…</div>;
  }

  if (effectiveMaintenanceDisabled && !isAdmin && !isBypassRoute) {
    return (
      <Maintenance
        message={maintenanceMessage}
        endsAt={maintenanceEndsAt}
        updatedAt={maintenanceUpdatedAt}
        updatedBy={maintenanceUpdatedBy}
        initiatedBy={maintenanceInitiatedBy}
      />
    );
  }

  return (
    <div
      className={`page${bannerEnabled ? ' has-banner' : ''}`}
      data-theme={theme}
      style={{
        backgroundImage:
          "url('https://github.com/Something-Comicallyfunny/somethingcomicallyphotoy/blob/main/title.PNG?raw=true')",
      }}
      ref={pageRef}
      onMouseMove={handleMouseMove}
    >
      {/* Top Navigation */}
      <header className="navbar" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <div className="nav-inner">
          <div className="nav-brand">Something Comical - - Ezra Dixon</div>
          <button
            type="button"
            className="nav-toggle"
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={navOpen ? 'true' : 'false'}
            onClick={() => setNavOpen((v) => !v)}
          >
            {navOpen ? '✕' : '☰'}
          </button>
          <nav className="nav-links" aria-label="Primary">
            <NavLink className={navLinkClass} to="/">Home</NavLink>
            <NavLink className={navLinkClass} to="/about">About</NavLink>
            <NavLink className={navLinkClass} to="/beliefs">Beliefs</NavLink>
            <NavLink className={navLinkClass} to="/liveries">Liveries</NavLink>
            <NavLink className={navLinkClass} to="/forums">Forums</NavLink>
            <NavAuthButtons />
          </nav>
          <nav className={navOpen ? 'nav-links nav-links-mobile open' : 'nav-links nav-links-mobile'} aria-label="Primary mobile">
            <NavLink className={navLinkClass} to="/">Home</NavLink>
            <NavLink className={navLinkClass} to="/about">About</NavLink>
            <NavLink className={navLinkClass} to="/beliefs">Beliefs</NavLink>
            <NavLink className={navLinkClass} to="/liveries">Liveries</NavLink>
            <NavLink className={navLinkClass} to="/forums">Forums</NavLink>
            <NavAuthButtons />
          </nav>
        </div>
      </header>
      {bannerEnabled && (
        <div className={`site-banner site-banner-${bannerTone}`} role="status" aria-live="polite">
          <div className="site-banner-inner">
            <span className="site-banner-text">{banner.text}</span>
            {banner.linkUrl ? (
              <a
                className="site-banner-link"
                href={banner.linkUrl}
                target={banner.linkUrl.startsWith('http') ? '_blank' : undefined}
                rel={banner.linkUrl.startsWith('http') ? 'noreferrer' : undefined}
              >
                {bannerLinkLabel}
              </a>
            ) : null}
          </div>
        </div>
      )}
      {/* Animated background blobs */}
      <div className="bg-blobs" aria-hidden>
        <span className="blob b1" />
        <span className="blob b2" />
        <span className="blob b3" />
      </div>

      {/* Overlay tint */}
      <div className="page-overlay" />

      <div className="content" id="top">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter' }}>
                <Home />
              </motion.div>
            )} />
            <Route
              path="/about"
              element={(
                <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                            exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                            transition={{ duration: 0.28, ease: 'easeOut' }}
                            style={{ willChange: 'opacity, transform, filter' }}>
                  <div style={{ maxWidth: 1100, margin: '0 auto', textAlign: 'center' }}>
                    <h1 className="title" style={{ textAlign: 'center' }}>About</h1>
                    <p className="subtitle" style={{ textAlign: 'center' }}>A bit about me and what I do</p>
                    <div className="sections-grid" id="about" style={{ justifyContent: 'center' }}>
                      <SectionList
                        sections={mainSections}
                        theme={theme}
                        expandedIds={expandedIds}
                        handleExpand={handleExpand}
                      />
                    </div>
                    {thanksSection && (
                      <div className="thanks-wrapper" id="thanks" style={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{ maxWidth: 1100, width: '100%' }}>
                          <SectionList
                            sections={[thanksSection]}
                            theme={theme}
                            expandedIds={expandedIds}
                            handleExpand={handleExpand}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            />
            <Route path="/beliefs" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter' }}>
                <Beliefs />
              </motion.div>
            )} />
            <Route path="/liveries" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter' }}>
                <Liveries />
              </motion.div>
            )} />
            <Route path="/forums" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter' }}>
                <Forums />
              </motion.div>
            )} />
            <Route path="/forums/:threadId" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter' }}>
                <Thread />
              </motion.div>
            )} />
            <Route path="/profile" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter' }}>
                <Profile />
              </motion.div>
            )} />
            <Route path="/login" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter' }}>
                <Login />
              </motion.div>
            )} />
            <Route path="/register" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter' }}>
                <Register />
              </motion.div>
            )} />
            <Route path="/reset-password" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter' }}>
                <ResetPassword />
              </motion.div>
            )} />
            <Route path="/admin" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter' }}>
                <Admin />
              </motion.div>
            )} />
            <Route path="/secret" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter' }}>
                {isAdmin ? <SecretPen /> : 'Not Found'}
              </motion.div>
            )} />
            <Route path="*" element={(
              <motion.div initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          style={{ willChange: 'opacity, transform, filter', paddingTop: '4rem' }}>
                Not Found
              </motion.div>
            )} />
          </Routes>
        </AnimatePresence>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <RolesProvider>
        <AppConfigProvider>
          <AppContent />
        </AppConfigProvider>
      </RolesProvider>
    </AuthProvider>
  );
}

export default App;
